import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ColOption, Table, TableRow, usePagination } from "@coolset/cs-ui";
import { cacheKeys, routes } from "config";
import { useQueryClient } from "react-query";
import { format } from "date-fns";
import { TitleCell } from "shared/components/Table/TitleCell";
import { useSurveysData } from "../hooks/useSurveysData";
import { useNavigate } from "react-router-dom";
import { surveysClient } from "../client/surveysClient";
import { SurveyStatus } from "./SurveyStatus";
import { KababMenu } from "shared/components/Menu/KababMenu";
import { Button, Dialog, DialogActions, MenuItem, Typography } from "@mui/material";
import { Publications } from "modules/Publications/publications";
import { StatusChip } from "shared/components/Chips/StatusChip";
import { LoadingButton } from "@mui/lab";

const TitleCellClass = "_d-flex-ali-center-jc-sb-sm-gap title-cell";

export const SurveyTable = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const paginationStore = usePagination("surveyPageSize");
  const { surveys, surveysCount, isLoading } = useSurveysData(paginationStore.apiParams);
  useEffect(() => paginationStore.setRowCount(surveysCount), [paginationStore, surveysCount]);

  const [surveyToDelete, setSurveyToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const colOptions = useMemo<ColOption[]>(
    () => [
      { label: "Title", width: "minmax(400px, 3fr)" },
      { label: "Status", width: "1fr" },
      { label: "Tags", width: "minmax(200px, 2fr)" },
      { label: "Created", width: "minmax(200px, 1fr)" },
      { label: "Modified", width: "minmax(200px, 1fr)" },
      { label: "Publish", width: "minmax(200px, 1fr)" },
      { label: "", width: "auto" },
    ],
    []
  );

  const handleSurveyClick = useCallback(
    (id: string | number) => {
      navigate(routes.survey(id));
    },
    [navigate]
  );

  const handleDeleteSurvey = useCallback(
    async (surveyId: number) => {
      setIsDeleting(true);
      await surveysClient.deleteSurvey(surveyId);
      queryClient.invalidateQueries(cacheKeys.surveys.getSurveys);
      setSurveyToDelete(null);
      setIsDeleting(false);
    },
    [queryClient]
  );

  const rows = useMemo<TableRow[]>(
    () =>
      surveys.map((survey) => ({
        cells: [
          {
            content: <TitleCell title={survey.title} />,
            className: TitleCellClass,
            onClick: () => handleSurveyClick(survey.id),
          },
          { content: <SurveyStatus publications={survey.publications} /> },
          {
            content: (
              <div className="_d-flex-sm-gap-f-wrap">
                {survey.tags.map((t) => (
                  <StatusChip label={t.name} key={t.name} />
                ))}
              </div>
            ),
          },
          { content: format(new Date(survey.createdAt), "dd MMM ''yy h:mm aaa"), isOneLine: true },
          { content: format(new Date(survey.updatedAt), "dd MMM ''yy h:mm aaa"), isOneLine: true },
          { content: getPublicationDate(survey.publications), isOneLine: true },
          {
            content: (
              <KababMenu
                containerProps={{ size: "small" }}
                closeOnClick
                items={[
                  <MenuItem key="open" onClick={() => handleSurveyClick(survey.id)}>
                    Open
                  </MenuItem>,
                  <MenuItem key="delete" onClick={() => setSurveyToDelete(survey.id)}>
                    Delete
                  </MenuItem>,
                ]}
              />
            ),
          },
        ],
      })),
    [handleSurveyClick, surveys]
  );

  return (
    <>
      <Dialog open={!!surveyToDelete} onClose={() => setSurveyToDelete(null)}>
        <Typography variant="h2" p={2} pb={5}>
          Are you sure you want to delete this survey?
        </Typography>
        <DialogActions>
          <Button onClick={() => setSurveyToDelete(null)}>Cancel</Button>
          <LoadingButton
            onClick={() => handleDeleteSurvey(surveyToDelete!)}
            color="error"
            variant="contained"
            loading={isDeleting}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Table colOptions={colOptions} rows={rows} isLoading={isLoading} paginationStore={paginationStore} />
    </>
  );
};

const getPublicationDate = (publications: Publications.Publication[]) => {
  let date: Date | undefined;
  publications.forEach((pub) => {
    const pubDate = new Date(pub.updatedAt);
    if (!date || pubDate > date) {
      date = pubDate;
    }
  });
  return date ? format(date, "dd MMM ''yy h:mm aaa") : "-";
};
